import { useState } from 'react';

import {
  Badge,
  DifficultyBadge,
  IconButton,
  OffsetPagination,
  Table,
  TableCard,
  TableSkeleton,
} from '@opositandopn/ui';
import { IoCreateOutline } from 'react-icons/io5';

import { BaseComponentProps } from '../../../../types/base-component.types';
import {
  useGetTrainingPlan,
  useGetTrainingPlans,
} from '../../api/usePhysicalTests';
import { TrainingPlanEditor } from '../training-plan-editor/TrainingPlanEditor';

import styles from './TrainingPlanAdminList.module.scss';

const fromTime = new Date(
  new Date().setFullYear(new Date().getFullYear() - 10),
);

function TrainingPlanAdminList({
  className,
  testId = 'ui-training-plan-admin-list',
}: BaseComponentProps) {
  const [currentId, setCurrentId] = useState<string | undefined>();
  const [pagination, setPagination] = useState({ pageNumber: 1, pageSize: 20 });

  const { data: response, isLoading } = useGetTrainingPlans({
    fromTime,
    pageNumber: pagination.pageNumber,
    pageSize: pagination.pageSize,
    sort: '-createdAt',
  });

  const { data: current } = useGetTrainingPlan(
    currentId ?? '',
    currentId !== undefined,
  );

  return (
    <>
      <TableCard.Root className={className} data-testid={testId}>
        <TableCard.Header
          title="Planes de entrenamiento"
          contentTrailing={
            <Badge>
              {response?.totalItems
                ? `Total: ${response?.totalItems}`
                : 'Sin planes'}
            </Badge>
          }
        />
        <Table aria-label="Planes de entrenamiento">
          <Table.Header>
            <Table.Head id="name" label="Nombre" isRowHeader />
            <Table.Head id="difficultyLevel" label="Dificultad" />
            <Table.Head id="actions" />
          </Table.Header>
          {isLoading || response === undefined ? (
            <TableSkeleton columns={3} items={pagination.pageSize} />
          ) : (
            <Table.Body
              items={response.items}
              renderEmptyState={() => 'No hay planes de entrenamiento'}
            >
              {(item) => (
                <Table.Row id={item.id}>
                  <Table.Cell
                    style={{
                      width: '100%',
                      wordWrap: 'break-word',
                      whiteSpace: 'normal',
                    }}
                  >
                    {item.name}
                  </Table.Cell>
                  <Table.Cell>
                    <DifficultyBadge difficulty={item.difficultyLevel} />
                  </Table.Cell>
                  <Table.Cell>
                    <div className={styles['actions']}>
                      <IconButton
                        size="sm"
                        icon={<IoCreateOutline />}
                        onClick={() => {
                          setCurrentId(item.id);
                        }}
                      />
                    </div>
                  </Table.Cell>
                </Table.Row>
              )}
            </Table.Body>
          )}
        </Table>
        {response && (
          <OffsetPagination
            className={styles.pagination}
            pageNumber={pagination.pageNumber}
            pageSize={pagination.pageSize}
            totalRecords={response.totalItems}
            onPageChange={(pageNumber: number) => {
              setPagination({ ...pagination, pageNumber });
            }}
            pageNeighbours={2}
          />
        )}
      </TableCard.Root>
      <TrainingPlanEditor
        open={currentId !== undefined && current !== undefined}
        trainingPlan={current}
        onClose={() => setCurrentId(undefined)}
      />
    </>
  );
}

export { TrainingPlanAdminList };
